import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import FeeCard from './FeeCard';
import { EqualisationFeesFormValues } from './FormWrapper';
import { CurrencyCode } from 'further-types/lp/vehicle';
import EqualisationFooter from '../EqualisationFooter';
import { useParams } from 'react-router-dom';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useEqualisationSection } from '../EqualisationContext';
import api from 'lib/trpcClient';
import { EqualisationSection } from 'further-types/lp/equalisation';
import EqualisationsTableHeader from 'components/EqualisationElements/EqualisationsTableHeader';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';

interface EqualiseFeesContentProps {
  lpFeeTemplates: Array<{
    id: string;
    name: string;
  }>;
  baseCurrency: CurrencyCode;
}

const EqualiseFeesContent: React.FC<EqualiseFeesContentProps> = ({
  lpFeeTemplates,
}) => {
  const { getValues } = useFormContext<EqualisationFeesFormValues>();
  const { previousSection, nextSection, goToNextSection, goToPreviousSection } =
    useEqualisationSection();
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();

  const equalisationActionAllocationsQueryKey = getQueryKey(
    api.equalisations.getEqualisationFees,
    {
      equalisationId: id ?? '',
      vehicleId: selectedVehicleId ?? '',
      feeTemplateOverrides: [],
    },
  );

  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation()({
      invalidations: [equalisationActionAllocationsQueryKey],
    }),
  );

  const totalCommitmentFees = useWatch({
    name: 'totalCommitmentFees',
  });

  const handleNext = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: nextSection ?? EqualisationSection.Summary,
        totalCommitmentFees: getValues('totalCommitmentFees').map((fee) => ({
          ...fee,
          lpFeeTemplateId:
            fee.lpFeeTemplateId !== 'none' ? fee.lpFeeTemplateId : undefined,
        })),
      },
      {
        onSuccess: () => {
          goToNextSection();
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
      },
      {
        onSuccess: () => {
          goToPreviousSection();
        },
      },
    );
  };

  return (
    <>
      <EqualisationsTableHeader
        headers={[
          'Investor name',
          'Status',
          'Fee template',
          'Managment fee equalisation treatment',
          '',
          'Fees outstanding',
        ]}
        gridTemplateColumns="20% 16% 16% 20% 12% 14%"
      />
      {totalCommitmentFees?.map((_, index) => (
        <FeeCard
          key={index}
          index={index}
          isLastElement={index === totalCommitmentFees.length - 1}
          lpFeeTemplates={lpFeeTemplates}
        />
      ))}
      <EqualisationFooter
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        disabled={false}
        loading={updateEqualisation.isPending}
      />
    </>
  );
};

export default EqualiseFeesContent;
